import React, { useState, useContext } from 'react';
import { Menu, Bell, LogOut, Settings, ChevronDown, Sun, Moon } from 'lucide-react';
import useAuth from '../../../hooks/useAuth';
import { getInitials } from '../../../utils/helpers';
import { ThemeContext } from '../../../context/ThemeContext';

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);

  let toggleTheme, isDark;
  try {
    const theme = useContext(ThemeContext);
    toggleTheme = theme?.toggleTheme;
    isDark = theme?.isDark;
  } catch (_) {}

  const initials = getInitials(user?.name || 'U');

  return (
    <header className="
      bg-white/90 dark:bg-slate-900/90
      backdrop-blur-md
      border-b border-slate-200 dark:border-slate-800
      sticky top-0 z-40
      transition-colors duration-200
    ">
      <div className="flex items-center justify-between px-4 py-3 lg:px-6">

        {/* Left */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors lg:hidden"
            aria-label="Toggle menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Mara Labs" className="h-7 w-auto" />
            <span className="text-lg font-bold gradient-text font-display hidden sm:block">
              Mara Labs
            </span>
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1.5">

          {/* Theme toggle */}
          {toggleTheme && (
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              title={isDark ? 'Modo claro' : 'Modo escuro'}
            >
              {isDark
                ? <Sun  className="w-4.5 h-4.5" style={{width:'18px',height:'18px'}} />
                : <Moon className="w-4.5 h-4.5" style={{width:'18px',height:'18px'}} />
              }
            </button>
          )}

          {/* Notifications */}
          <button className="relative p-2 rounded-lg text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
            <Bell className="w-4.5 h-4.5" style={{width:'18px',height:'18px'}} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full ring-2 ring-white dark:ring-slate-900" />
          </button>

          {/* User */}
          <div className="relative ml-1">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2.5 pl-1 pr-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="w-7 h-7 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-xs">{initials}</span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 leading-tight">
                  {user?.name || 'Usuário'}
                </p>
                <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate max-w-[140px]">
                  {user?.email || ''}
                </p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden md:block" />
            </button>

            {showDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
                <div className="
                  absolute right-0 mt-2 w-56
                  bg-white dark:bg-slate-800
                  rounded-xl shadow-dropdown
                  border border-slate-200 dark:border-slate-700
                  py-1.5 z-50
                  animate-slide-down
                ">
                  <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-700 mb-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {user?.name || 'Usuário'}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {user?.email || ''}
                    </p>
                  </div>

                  <button className="w-full px-4 py-2 text-left text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors">
                    <Settings className="w-4 h-4 text-slate-400" />
                    Configurações
                  </button>

                  <div className="border-t border-slate-100 dark:border-slate-700 mt-1 pt-1">
                    <button
                      onClick={logout}
                      className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2.5 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Sair
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
